import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { toCurrency } from "./shopUtils";

export default function ShopCartPanel({
  cartItems,
  setCartItems,
  cartTotal,
  cartTotalItems,
  onCheckout,
  creatingOrder,
  hasContractContext,
  checkoutDisabledMessage,
  checkoutContextNote,
  isMemberCatalogView,
  variantInventoryByKey,
  hideHeader,
}) {
  const Wrapper = hideHeader ? Box : Paper;
  const wrapperProps = hideHeader
    ? {}
    : {
        variant: "outlined",
        sx: {
          borderRadius: 2.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: cartTotalItems > 0 ? "primary.light" : "divider",
          transition: "border-color 0.3s",
        },
      };

  return (
    <Wrapper {...wrapperProps}>
      {/* Header (hidden in drawer mode) */}
      {!hideHeader ? (
        <Box
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Iconify
              icon="eva:shopping-cart-fill"
              width={22}
              sx={{
                color: cartTotalItems > 0 ? "primary.main" : "text.disabled",
              }}
            />
            <Typography variant="h6" fontWeight={700}>
              Warenkorb
            </Typography>
            {cartTotalItems > 0 ? (
              <Chip
                label={`${cartTotalItems} Artikel`}
                size="small"
                color="primary"
                variant="filled"
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <Box sx={{ p: 2.5 }}>
        {!cartItems.length ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Iconify
              icon="eva:shopping-cart-outline"
              width={72}
              sx={{ color: "grey.300", mb: 2 }}
            />
            <Typography
              variant="body1"
              color="text.secondary"
              fontWeight={500}
              gutterBottom
            >
              Dein Warenkorb ist leer
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Füge Artikel aus dem Katalog hinzu
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {cartItems.map((item, index) => {
              const inventoryKey = `${item.productId}::${item.variantKey}`;
              const inventory = variantInventoryByKey?.[inventoryKey];
              const stockLimit =
                Number.isInteger(inventory?.stock)
                  ? inventory.stock
                  : Number.isInteger(item.availableStock)
                    ? item.availableStock
                    : null;
              const isInStock =
                typeof inventory?.isInStock === "boolean"
                  ? inventory.isInStock
                  : item.isInStock !== false;
              const canShowStockCount =
                !isMemberCatalogView || Boolean(inventory?.showStockToMembers);
              const canIncrease =
                isInStock && (stockLimit === null || Number(item.quantity) < stockLimit);

              return (
                <Paper
                  key={`${item.productId}-${item.variantKey}`}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 1.5 }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          noWrap
                        >
                          {item.productName}
                        </Typography>
                        {item.variantLabel &&
                        item.variantLabel !== "Standard" ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {item.variantLabel}
                          </Typography>
                        ) : null}
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setCartItems((prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                      >
                        <Iconify icon="eva:trash-2-outline" width={16} />
                      </IconButton>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            setCartItems((prev) =>
                              prev.map((e, i) =>
                                i === index
                                  ? {
                                      ...e,
                                      quantity: Math.max(
                                        1,
                                        Number(e.quantity) - 1,
                                      ),
                                    }
                                  : e,
                              ),
                            )
                          }
                          sx={{
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            width: 28,
                            height: 28,
                          }}
                          disabled={Number(item.quantity) <= 1}
                        >
                          <Iconify icon="eva:minus-fill" width={14} />
                        </IconButton>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ minWidth: 32, textAlign: "center" }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setCartItems((prev) =>
                              prev.map((e, i) =>
                                i === index
                                  ? {
                                      ...e,
                                      quantity:
                                        stockLimit === null
                                          ? Number(e.quantity) + 1
                                          : Math.min(Number(e.quantity) + 1, stockLimit),
                                    }
                                  : e,
                              ),
                            )
                          }
                          sx={{
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            width: 28,
                            height: 28,
                          }}
                          disabled={!canIncrease}
                        >
                          <Iconify icon="eva:plus-fill" width={14} />
                        </IconButton>
                      </Stack>

                      <Typography
                        variant="subtitle2"
                        color="primary.main"
                        fontWeight={700}
                      >
                        {toCurrency(
                          Number(item.unitPrice) * Number(item.quantity),
                        )}
                      </Typography>
                    </Stack>

                    {isInStock ? (
                      stockLimit !== null && canShowStockCount ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          Bestand: {stockLimit} Stueck
                        </Typography>
                      ) : null
                    ) : (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ display: "block" }}
                      >
                        Nicht mehr verfuegbar.
                      </Typography>
                    )}

                    {Number(item.quantity) > 1 ? (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        align="right"
                        sx={{ display: "block" }}
                      >
                        {toCurrency(item.unitPrice)} &times; {item.quantity} Stück
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              );
            })}

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 0.5 }}
            >
              <Typography variant="body1" fontWeight={500}>
                Gesamtbetrag
              </Typography>
              <Typography
                variant="h6"
                color="primary.main"
                fontWeight={700}
              >
                {toCurrency(cartTotal)}
              </Typography>
            </Stack>

            {cartItems.some((item) => Number(item.taxRate || 0) > 0) ? (
              <Typography variant="caption" color="text.secondary">
                Alle Preise inkl. MwSt.
              </Typography>
            ) : null}

            {checkoutContextNote ? (
              <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                {checkoutContextNote}
              </Alert>
            ) : null}

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onCheckout}
              disabled={
                !cartItems.length || creatingOrder || !hasContractContext
              }
              startIcon={
                <Iconify icon="eva:credit-card-fill" width={20} />
              }
              sx={{
                py: 1.5,
                fontWeight: 700,
                fontSize: "1rem",
                borderRadius: 2,
                mt: 0.5,
              }}
            >
              Zur Kasse
            </Button>

            {!hasContractContext && checkoutDisabledMessage ? (
              <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                {checkoutDisabledMessage}
              </Alert>
            ) : null}
          </Stack>
        )}
      </Box>
    </Wrapper>
  );
}
